<script setup lang="ts">
const recommendedJobs = [
  { title: "Front Desk Agent", company: "Safari Lodge", location: "Cape Town", match: 95, logo: "material-symbols:hotel" },
  { title: "Restaurant Server", company: "Dolci Cafe", location: "Johannesburg", match: 88, logo: "material-symbols:restaurant" },
  { title: "Concierge", company: "Beach Resort", location: "Durban", match: 82, logo: "material-symbols:beach-access" },
];
</script>

<template>
  <div class="relative">
    <!-- Main Jobs List Card -->
    <div class="rounded-3xl 3xl:rounded-[2rem] overflow-hidden shadow-warm-lg bg-white border border-sand/50">
      <!-- Header -->
      <div class="bg-gradient-to-r from-gold-dark to-gold p-3 sm:p-5 2xl:p-6 3xl:p-8 4xl:p-10">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2 sm:gap-3 2xl:gap-4">
            <div
              class="w-8 h-8 sm:w-10 sm:h-10 2xl:w-12 2xl:h-12 3xl:w-14 3xl:h-14 4xl:w-16 4xl:h-16 rounded-lg sm:rounded-xl 2xl:rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0"
            >
              <Icon name="material-symbols:work" class="w-4 h-4 sm:w-5 sm:h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7 4xl:w-8 4xl:h-8 text-white" />
            </div>
            <span class="text-white font-semibold text-sm sm:text-base 2xl:text-lg 3xl:text-xl 4xl:text-2xl">Recommended Jobs</span>
          </div>
          <div class="bg-white/20 rounded-full px-2 py-0.5 sm:px-3 sm:py-1 2xl:px-4 2xl:py-1.5 3xl:px-5 3xl:py-2">
            <span class="text-white text-xs sm:text-sm 2xl:text-base 3xl:text-lg font-medium">12 new</span>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="p-3 sm:p-4 2xl:p-6 3xl:p-8 4xl:p-10 space-y-2 sm:space-y-3 2xl:space-y-4 3xl:space-y-5">
        <div
          v-for="(job, index) in recommendedJobs"
          :key="job.title"
          class="group p-3 sm:p-4 2xl:p-5 3xl:p-6 rounded-xl sm:rounded-2xl 2xl:rounded-3xl border border-sand hover:border-primary/30 hover:bg-cream/50 transition-all cursor-pointer"
        >
          <div class="flex items-start gap-2 sm:gap-3 2xl:gap-4 3xl:gap-5">
            <!-- Company Logo -->
            <div
              class="w-9 h-9 sm:w-12 sm:h-12 2xl:w-14 2xl:h-14 3xl:w-16 3xl:h-16 4xl:w-20 4xl:h-20 rounded-lg sm:rounded-xl 2xl:rounded-2xl bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center flex-shrink-0"
            >
              <Icon :name="job.logo" class="w-4 h-4 sm:w-6 sm:h-6 2xl:w-7 2xl:h-7 3xl:w-8 3xl:h-8 4xl:w-10 4xl:h-10 text-primary" />
            </div>

            <!-- Job Info -->
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between gap-1.5 sm:gap-2 mb-0.5 sm:mb-1 2xl:mb-2">
                <h4 class="text-sm sm:text-base 2xl:text-lg 3xl:text-xl 4xl:text-2xl font-semibold text-dark-gray truncate">
                  {{ job.title }}
                </h4>
                <div class="bg-savanna/10 rounded-full px-1.5 py-0.5 sm:px-2 2xl:px-3 2xl:py-1 3xl:px-4 3xl:py-1.5 flex-shrink-0">
                  <span class="text-[10px] sm:text-xs 2xl:text-sm 3xl:text-base font-bold text-savanna">{{ job.match }}%</span>
                </div>
              </div>
              <p class="text-xs sm:text-sm 2xl:text-base 3xl:text-lg text-medium-gray mb-1 sm:mb-2 2xl:mb-3">{{ job.company }}</p>
              <div class="flex items-center gap-1 sm:gap-2 text-[10px] sm:text-xs 2xl:text-sm 3xl:text-base text-medium-gray">
                <Icon name="material-symbols:location-on" class="w-3 h-3 sm:w-4 sm:h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6" />
                <span>{{ job.location }}</span>
              </div>
            </div>
          </div>

          <!-- Quick Apply Button (shows on hover) -->
          <div class="mt-2 sm:mt-3 2xl:mt-4 3xl:mt-5 flex justify-end">
            <button
              class="px-3 py-1.5 sm:px-4 sm:py-2 2xl:px-5 2xl:py-2.5 3xl:px-6 3xl:py-3 rounded-lg sm:rounded-xl 2xl:rounded-2xl bg-primary text-white text-xs sm:text-sm 2xl:text-base 3xl:text-lg font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
            >
              Quick Apply
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Floating Notification Card -->
    <div
      class="hidden lg:block absolute -right-4 lg:-right-8 2xl:-right-12 3xl:-right-16 top-1/4 z-20 bg-white rounded-2xl 3xl:rounded-3xl shadow-warm-lg p-4 2xl:p-5 3xl:p-6 animate-gentle-float max-w-[200px] 2xl:max-w-[240px] 3xl:max-w-[280px]"
      style="animation-delay: 0.3s"
    >
      <div class="flex items-start gap-3 2xl:gap-4">
        <div
          class="w-10 h-10 2xl:w-12 2xl:h-12 3xl:w-14 3xl:h-14 rounded-xl 2xl:rounded-2xl bg-secondary/10 flex items-center justify-center flex-shrink-0"
        >
          <Icon name="material-symbols:notifications-active" class="w-5 h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7 text-secondary" />
        </div>
        <div>
          <p class="text-xs 2xl:text-sm 3xl:text-base font-semibold text-dark-gray">New Job Alert!</p>
          <p class="text-xs 2xl:text-sm 3xl:text-base text-medium-gray mt-1">5 new jobs match your profile</p>
        </div>
      </div>
    </div>

    <!-- Floating Invite Badge -->
    <div
      class="hidden lg:flex absolute -left-4 lg:-left-8 2xl:-left-12 bottom-1/3 z-20 bg-gradient-to-r from-gold to-gold-light rounded-full shadow-gold px-4 py-2 2xl:px-6 2xl:py-3 3xl:px-8 3xl:py-4 4xl:px-10 4xl:py-5 animate-gentle-float"
      style="animation-delay: 0.6s"
    >
      <div class="flex items-center gap-2 2xl:gap-3">
        <div class="w-8 h-8 2xl:w-10 2xl:h-10 3xl:w-12 3xl:h-12 rounded-lg 2xl:rounded-xl bg-white/20 flex items-center justify-center">
          <Icon name="material-symbols:mail" class="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 text-white" />
        </div>
        <div>
          <p class="text-xs 2xl:text-sm 3xl:text-base text-white/80">You've been invited!</p>
          <p class="text-sm 2xl:text-base 3xl:text-lg font-semibold text-white">Safari Lodge</p>
        </div>
      </div>
    </div>

    <!-- Decorative blur -->
    <div class="absolute -z-10 -top-8 -left-8 w-48 h-48 2xl:w-64 2xl:h-64 3xl:w-80 3xl:h-80 bg-gold/10 rounded-full blur-3xl"></div>
  </div>
</template>
